import { useEffect, useState, type FormEvent } from 'react';
import {
  BadgePercent, Plus, Pencil, CheckCircle2, Eye, EyeOff, Loader2, AlertCircle, ExternalLink,
} from 'lucide-react';
import {
  listIncentivePrograms, createIncentiveProgram, updateIncentiveProgram,
  getDisplayErrorMessage,
  type IncentiveProgramRow, type IncentiveUpsertPayload,
} from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { Modal } from '@/components/ui/Modal';
import formStyles from '@/components/ui/Form.module.css';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './IncentiveProgramsPage.module.css';
import { Switch } from '@/components/ui/Switch';

/**
 * Incentive Programs editor (incentives v2) — the monthly-review surface for the
 * curated incentive layer behind the shop's /incentives page. List, create,
 * edit, status flip, publish toggle, and verified-today touch. super_admin /
 * operator only (the /admin/incentives routes are the security boundary; every
 * mutation is audited server-side).
 */

const COUNTRIES = ['US', 'CA'] as const;
const SCOPE_TAGS = ['utility', 'air_district', 'nonprofit', 'state_extra', 'federal', 'agency'] as const;
const STATUSES = ['active', 'waitlist', 'paused', 'closed'] as const;
const PROPERTY_TYPES = ['residential', 'commercial', 'multifamily', 'fleet'] as const;

interface IncentiveProgramsPageProps { onNavigate?: (to: string) => void }

function verifiedLabel(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function IncentiveProgramsPage(_props: IncentiveProgramsPageProps = {}) {
  useRecentRoute('Incentive Programs');
  const role = useAuthStore((s) => s.user?.role);
  const hasAccess = role === 'super_admin' || role === 'operator';

  const [rows, setRows] = useState<IncentiveProgramRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<IncentiveProgramRow | 'new' | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null);

  const reload = async (): Promise<void> => {
    setLoading(true);
    setError('');
    try {
      setRows(await listIncentivePrograms());
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Failed to load incentive programs'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasAccess) {
      setLoading(false);
      setError('Incentive Programs is super_admin or operator only.');
      return;
    }
    // reload is a stable closure on this component; intentionally not in the
    // dep array (would loop on every render).
    void reload();
  }, [hasAccess]);

  const quickPatch = async (row: IncentiveProgramRow, body: IncentiveUpsertPayload): Promise<void> => {
    if (rowBusy) return;
    setRowBusy(row.id);
    setError('');
    try {
      const updated = await updateIncentiveProgram(row.id, body);
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Update failed'));
    } finally {
      setRowBusy(null);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerIcon}><BadgePercent size={18} /></div>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>Incentive Programs</h1>
          <p className={styles.subtitle}>
            The hand-verified curated layer behind the shop&rsquo;s incentives page, merged with
            live AFDC data. Review monthly: touch Verified when you confirm a program, flip status
            or unpublish when one changes.
          </p>
        </div>
        {hasAccess && (
          <button type="button" className={styles.createBtn} onClick={() => setEditing('new')} disabled={loading}>
            <Plus size={14} /> New program
          </button>
        )}
      </div>

      {loading && (
        <div className={styles.empty}><Loader2 size={18} className={styles.spin} /> Loading…</div>
      )}
      {!loading && error && (
        <div className={styles.error}><AlertCircle size={16} /> {error}</div>
      )}
      {!loading && !error && rows.length === 0 && (
        <div className={styles.empty}>No programs yet. Add one, or run the curated seed.</div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Program</th>
                <th>Region</th>
                <th>Scope</th>
                <th>Equipment</th>
                <th>Status</th>
                <th>Published</th>
                <th>Verified</th>
                <th aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={r.is_published ? '' : styles.rowUnpublished}>
                  <td>
                    <div className={styles.progName}>
                      {r.name}{' '}
                      <a href={r.url} target="_blank" rel="noreferrer" aria-label="Open program page">
                        <ExternalLink size={11} />
                      </a>
                    </div>
                    <div className={styles.progAdmin}>{r.administrator}</div>
                  </td>
                  <td>{r.country} · {r.region}</td>
                  <td className={styles.muted}>{r.scope_tag}</td>
                  <td>
                    <span className={styles.equip}>
                      <span className={`${styles.equipTag} ${r.equipment.dc_fast ? styles.equipOn : ''}`}>DC</span>
                      <span className={`${styles.equipTag} ${r.equipment.level2_ac ? styles.equipOn : ''}`}>L2</span>
                      <span className={`${styles.equipTag} ${r.equipment.make_ready ? styles.equipOn : ''}`}>MR</span>
                    </span>
                  </td>
                  <td>
                    <span className={`${styles.chip} ${r.status === 'active' ? styles.chipOk : r.status === 'closed' ? styles.chipMuted : styles.chipWarn}`}>
                      {r.status}
                    </span>
                  </td>
                  <td>
                    {r.is_published
                      ? <span className={`${styles.chip} ${styles.chipOk}`}>Live</span>
                      : <span className={`${styles.chip} ${styles.chipMuted}`}>Hidden</span>}
                  </td>
                  <td className={styles.muted}>{verifiedLabel(r.verified_at)}</td>
                  <td>
                    <span className={styles.rowActions}>
                      <button type="button" className={styles.iconBtn} onClick={() => setEditing(r)} disabled={rowBusy === r.id}>
                        <Pencil size={12} /> Edit
                      </button>
                      <button
                        type="button"
                        className={styles.iconBtn}
                        title="Mark verified today"
                        onClick={() => void quickPatch(r, { verified_at: 'now' })}
                        disabled={rowBusy === r.id}
                      >
                        {rowBusy === r.id ? <Loader2 size={12} className={styles.spin} /> : <CheckCircle2 size={12} />} Verify
                      </button>
                      <button
                        type="button"
                        className={styles.iconBtn}
                        title={r.is_published ? 'Unpublish' : 'Publish'}
                        onClick={() => void quickPatch(r, { is_published: !r.is_published })}
                        disabled={rowBusy === r.id}
                      >
                        {r.is_published ? <EyeOff size={12} /> : <Eye size={12} />}
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <IncentiveFormModal
          program={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await reload(); }}
        />
      )}
    </div>
  );
}

// ── Create / edit modal ───────────────────────────────────────────────────────
type FormState = {
  key: string; country: 'US' | 'CA'; region: string; administrator: string; name: string;
  scope_tag: string; property_types: string[];
  dc_fast: boolean; level2_ac: boolean; make_ready: boolean;
  amount: string; summary: string; eligibility_notes: string; deadline: string; url: string;
  status: 'active' | 'waitlist' | 'paused' | 'closed'; is_published: boolean;
};

function fromRow(p: IncentiveProgramRow | null): FormState {
  return {
    key: p?.key ?? '',
    country: p?.country ?? 'US',
    region: p?.region ?? '',
    administrator: p?.administrator ?? '',
    name: p?.name ?? '',
    scope_tag: p?.scope_tag ?? 'utility',
    property_types: p?.property_types ?? [],
    dc_fast: p?.equipment.dc_fast ?? false,
    level2_ac: p?.equipment.level2_ac ?? false,
    make_ready: p?.equipment.make_ready ?? false,
    amount: p?.amount ?? '',
    summary: p?.summary ?? '',
    eligibility_notes: p?.eligibility_notes ?? '',
    deadline: p?.deadline ?? '',
    url: p?.url ?? '',
    status: p?.status ?? 'active',
    is_published: p?.is_published ?? true,
  };
}

function IncentiveFormModal({
  program, onClose, onSaved,
}: { program: IncentiveProgramRow | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const isEdit = program !== null;
  const [f, setF] = useState<FormState>(() => fromRow(program));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setF((s) => ({ ...s, [k]: v }));
  const toggleProperty = (t: string) =>
    setF((s) => ({
      ...s,
      property_types: s.property_types.includes(t)
        ? s.property_types.filter((x) => x !== t)
        : [...s.property_types, t],
    }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr('');
    const equipment = { dc_fast: f.dc_fast, level2_ac: f.level2_ac, make_ready: f.make_ready };
    const common: IncentiveUpsertPayload = {
      country: f.country,
      region: f.region.trim().toUpperCase(),
      administrator: f.administrator.trim(),
      name: f.name.trim(),
      scope_tag: f.scope_tag,
      property_types: f.property_types,
      equipment,
      amount: f.amount.trim() || null,
      summary: f.summary.trim(),
      eligibility_notes: f.eligibility_notes.trim() || null,
      deadline: f.deadline.trim() || null,
      url: f.url.trim(),
      status: f.status,
      is_published: f.is_published,
    };
    try {
      if (isEdit && program) {
        await updateIncentiveProgram(program.id, common);
      } else {
        await createIncentiveProgram({ ...common, key: f.key.trim() });
      }
      await onSaved();
    } catch (e2) {
      setErr(await getDisplayErrorMessage(e2, 'Save failed'));
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={busy ? () => {} : onClose} title={isEdit ? 'Edit program' : 'New program'} size="lg">
      <form className={styles.form} onSubmit={onSubmit}>
        {!isEdit && (
          <label className={styles.field}>
            <span>Key (stable slug, unique)</span>
            <input value={f.key} onChange={(e) => set('key', e.target.value)} placeholder="pge-ev-power-ready" required />
          </label>
        )}
        <div className={styles.row2}>
          <label className={styles.field}>
            <span>Country</span>
            <select value={f.country} onChange={(e) => set('country', e.target.value as 'US' | 'CA')}>
              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className={styles.field}>
            <span>Region (state/province code, or FED)</span>
            <input value={f.region} onChange={(e) => set('region', e.target.value)} placeholder="CA / QC / FED" required />
          </label>
        </div>
        <label className={styles.field}>
          <span>Program name</span>
          <input value={f.name} onChange={(e) => set('name', e.target.value)} required />
        </label>
        <label className={styles.field}>
          <span>Administrator</span>
          <input value={f.administrator} onChange={(e) => set('administrator', e.target.value)} required />
        </label>
        <div className={styles.row2}>
          <label className={styles.field}>
            <span>Scope</span>
            <select value={f.scope_tag} onChange={(e) => set('scope_tag', e.target.value)}>
              {SCOPE_TAGS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className={styles.field}>
            <span>Status</span>
            <select value={f.status} onChange={(e) => set('status', e.target.value as FormState['status'])}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        </div>
        <div className={styles.field}>
          <span>Equipment funded</span>
          <div className={styles.checks}>
            <label className={styles.check}><input type="checkbox" checked={f.dc_fast} onChange={(e) => set('dc_fast', e.target.checked)} /> DC Fast</label>
            <label className={styles.check}><input type="checkbox" checked={f.level2_ac} onChange={(e) => set('level2_ac', e.target.checked)} /> Level 2 AC</label>
            <label className={styles.check}><input type="checkbox" checked={f.make_ready} onChange={(e) => set('make_ready', e.target.checked)} /> Make-ready</label>
          </div>
        </div>
        <div className={styles.field}>
          <span>Property types</span>
          <div className={styles.checks}>
            {PROPERTY_TYPES.map((t) => (
              <label key={t} className={styles.check}>
                <input type="checkbox" checked={f.property_types.includes(t)} onChange={() => toggleProperty(t)} /> {t}
              </label>
            ))}
          </div>
        </div>
        <label className={styles.field}>
          <span>Amount (as stated on the official page, or blank)</span>
          <input value={f.amount} onChange={(e) => set('amount', e.target.value)} placeholder="Up to $100,000 per site…" />
        </label>
        <label className={styles.field}>
          <span>Summary</span>
          <textarea value={f.summary} onChange={(e) => set('summary', e.target.value)} required />
        </label>
        <label className={styles.field}>
          <span>Eligibility notes (optional)</span>
          <textarea value={f.eligibility_notes} onChange={(e) => set('eligibility_notes', e.target.value)} />
        </label>
        <div className={styles.row2}>
          <label className={styles.field}>
            <span>Deadline (optional)</span>
            <input value={f.deadline} onChange={(e) => set('deadline', e.target.value)} placeholder="2028-03-31" />
          </label>
          <label className={styles.field}>
            <span>Program URL</span>
            <input value={f.url} onChange={(e) => set('url', e.target.value)} type="url" required />
          </label>
        </div>
        <Switch checked={f.is_published} onChange={(e) => set('is_published', e.target.checked)} label="Published (visible on the shop)" />

        {err && <p className={styles.formErr}>{err}</p>}
        <div className={styles.formActions}>
          <button type="button" className={formStyles.btnSecondary} onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className={formStyles.btnPrimary} disabled={busy}>
            {busy ? <><Loader2 size={13} className={styles.spin} /> Saving…</> : (isEdit ? 'Save changes' : 'Create program')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
